import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Info, X } from 'lucide-react';
import { useTopmostEscape } from '../../../hooks/useTopmostEscape';
import type { ActualsResponse } from '../../../types/strategicKpis';
import { scopeLabel, topGroup } from '../../../utils/strategicKpiSelectors';

/**
 * The chip row of /strategic-kpis (masterplan skpi-redesign §3 E4, mockup «activebar»; package A2a):
 * one chip per set list with × (as in the sales app: «Company  DIT, DPT» or «Person  A. Muster +2»),
 * «Reset all», the data state on the right, and the info icon that carries the text of the former
 * SelectionNotice (E4, Q7) in a small popover.
 *
 * No data logic in the component: chips, texts and handlers come in as props. `selectionInfo(data)`
 * below is the one pure helper — the SelectionNotice wording, unchanged, so the page can pass it on.
 * The info title is also spoken in a polite live region, as the notice's `role="status"` was.
 *
 * Focus: once the row without a removed chip is committed, focus moves to the next chip's ×, then to
 * «Reset all», then `onEmptied`
 * (the page sends it to the first list) — never to <body>.
 */

export interface ActiveChip {
    key: string;
    /** «Company», «Team», «Person» */
    kind: string;
    /** The value text on the chip. */
    value: string;
    /** Full list for the tooltip, one name per line. */
    title?: string;
    onRemove: () => void;
}

export interface DataStateLine {
    text: string;
    title?: string;
    /** Older than expected: amber dot and text. */
    stale?: boolean;
}

export interface SelectionInfo {
    /** «Filtered: Team DIT Sales · 4 persons» */
    title: string;
    text: string;
}

interface Props {
    chips: readonly ActiveChip[];
    /** Shown when there is no chip («No selection: the whole SPC business unit»). */
    emptyText: string;
    /** «Reset all»; omitted or without chips, no button. */
    onReset?: () => void;
    resetLabel?: string;
    /** Called when the last chip goes, so focus can land somewhere useful. */
    onEmptied?: () => void;
    dataState?: DataStateLine | null;
    info?: SelectionInfo | null;
    /** Extra content before the data state (none in phase A). */
    extra?: ReactNode;
}

const prefersReducedMotion = () => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return true; }
};
/** --dur-micro */
const LEAVE_MS = 150;

export function ActiveRow({ chips, emptyText, onReset, resetLabel = 'Reset all', onEmptied, dataState, info, extra }: Props) {
    const rowRef = useRef<HTMLDivElement>(null);
    const [leaving, setLeaving] = useState<ReadonlySet<string>>(() => new Set());
    /** One timer per leaving chip; all cleared on unmount. */
    const timers = useRef(new Map<string, number>());
    useEffect(() => {
        const pending = timers.current;
        return () => { pending.forEach(t => window.clearTimeout(t)); pending.clear(); };
    }, []);

    // The chips of the LATEST render. A removal runs up to 150 ms after its click; by then another chip may
    // have gone, and the closure from the click would write the old lists back (review A, finding 1). So the
    // removal looks its chip up here when it runs and calls the handler built on the current selection.
    const latest = useRef(chips);
    latest.current = chips;
    const latestEmptied = useRef(onEmptied);
    latestEmptied.current = onEmptied;

    /** Where focus goes once the row without the removed chip is committed: the slot it left. */
    const focusSlot = useRef<number | null>(null);
    const chipKeys = chips.map(c => c.key).join('|');
    useEffect(() => {
        const index = focusSlot.current;
        if (index === null) return;
        focusSlot.current = null;
        // After the commit (finding 5): the × now at `index` (the next chip), else «Reset all», else the page's target.
        const xs = rowRef.current?.querySelectorAll<HTMLButtonElement>('.chip .x');
        const next = xs && index < xs.length ? xs[index] : null;
        const reset = rowRef.current?.querySelector<HTMLButtonElement>('button.reset');
        if (next) next.focus();
        else if (reset) reset.focus();
        else latestEmptied.current?.();
    }, [chipKeys]);

    const remove = (key: string) => {
        const go = () => {
            timers.current.delete(key);
            setLeaving(prev => { if (!prev.has(key)) return prev; const n = new Set(prev); n.delete(key); return n; });
            const now = latest.current;
            const index = now.findIndex(c => c.key === key);
            if (index < 0) return;   // gone meanwhile (Reset all): nothing left to remove
            focusSlot.current = index;
            now[index].onRemove();
        };
        if (timers.current.has(key)) return;
        if (prefersReducedMotion()) { go(); return; }
        setLeaving(prev => new Set(prev).add(key));
        timers.current.set(key, window.setTimeout(go, LEAVE_MS));
    };

    return (
        <div ref={rowRef} className="activebar" role="region" aria-label="Active selection and data state" data-testid="skpi-activebar">
            <div className="chips">
                {chips.length === 0 && <span className="none">{emptyText}</span>}
                {chips.map(c => (
                    <span key={c.key} className={`chip${leaving.has(c.key) ? ' leaving' : ''}`} title={c.title}>
                        <span className="k">{c.kind}</span>
                        <span className="v">{c.value}</span>
                        <button type="button" className="x" aria-label={`Remove the ${c.kind.toLowerCase()} filter (${c.value})`} onClick={() => remove(c.key)}>
                            <X className="i sm" aria-hidden="true" />
                        </button>
                    </span>
                ))}
                {chips.length > 0 && onReset && (
                    <button type="button" className="reset" onClick={onReset} data-testid="selection-reset">{resetLabel}</button>
                )}
                {info && <InfoButton info={info} />}
            </div>
            {extra}
            {dataState && (
                <div className="datastate" title={dataState.title}>
                    <span className={dataState.stale ? 'stale' : undefined}>
                        <span className="dot" aria-hidden="true" /> {dataState.text}
                    </span>
                </div>
            )}
            <span className="sr-only" role="status">{info?.title ?? ''}</span>
        </div>
    );
}

/** The info icon and its popover (a disclosure: Escape and a click outside close it, focus returns). */
function InfoButton({ info }: { info: SelectionInfo }) {
    const [open, setOpen] = useState(false);
    const wrap = useRef<HTMLSpanElement>(null);
    const btn = useRef<HTMLButtonElement>(null);
    const popId = useId();

    useTopmostEscape(open, () => { setOpen(false); btn.current?.focus(); });
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    return (
        <span ref={wrap} className="relative inline-flex">
            <button
                ref={btn}
                type="button"
                className="info-btn"
                aria-expanded={open}
                aria-controls={open ? popId : undefined}
                aria-label="What the figures describe"
                onClick={() => setOpen(o => !o)}
                data-testid="selection-info"
            >
                <Info className="i" aria-hidden="true" />
            </button>
            {open && (
                <div id={popId} className="popover infopop absolute left-0 top-full mt-1" data-state="open" data-testid="selection-notice">
                    <h3>{info.title}</h3>
                    <p>{info.text}</p>
                </div>
            )}
        </span>
    );
}

/**
 * K3: what the figures describe when a selection is active — the engine's decision, in the words of the
 * former SelectionNotice (StrategicKpisPage, before A2b), unchanged. Null without a selection.
 */
export function selectionInfo(data: ActualsResponse): SelectionInfo | null {
    if (!data.selection) return null;
    const top = topGroup(data);
    if (!top) return null;
    const exact = data.selection.matches;
    const n = data.selection.staffIds.length;
    const label = scopeLabel(top);
    // "3 persons" is already its own count.
    const count = label === `${n} persons` ? '' : ` · ${n} ${n === 1 ? 'person' : 'persons'}`;
    const text = exact && top.scopeType !== 'SELECTION'
        ? `The selection is exactly this ${top.scopeType === 'TOTAL' ? 'request' : top.scopeType.toLowerCase()} — its group target applies where one is stored.`
        : 'No single team, company or BU — figures pooled over the selected persons; targets are the Σ of their person targets; GP is the Σ of the persons; BAB has no figure. Teams and companies the filter cuts are marked “n of m selected” in the matrix.';
    return { title: `Filtered: ${label}${count}`, text };
}
